import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { X, ShieldCheck } from "@phosphor-icons/react";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const digitsOnly = (s) => (s || "").replace(/\D/g, "");
const formatCardDisplay = (s) => digitsOnly(s).replace(/(.{4})/g, "$1 ").trim();

// Load Accept.js exactly once, matching the target environment.
let acceptJsPromise = null;
const loadAcceptJs = (environment) => {
  if (acceptJsPromise) return acceptJsPromise;
  const url =
    environment === "production"
      ? "https://js.authorize.net/v1/Accept.js"
      : "https://jstest.authorize.net/v1/Accept.js";
  acceptJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = url;
    s.async = true;
    s.charset = "utf-8";
    s.onload = () => resolve(window.Accept);
    s.onerror = () => {
      acceptJsPromise = null;
      reject(new Error(`Failed to load Accept.js from ${url}`));
    };
    document.head.appendChild(s);
  });
  return acceptJsPromise;
};

/**
 * Charge modal — PCI SAQ A-EP flow. The card number, expiry, and CVV are
 * captured in this form, immediately handed to Authorize.net's Accept.js
 * in the browser, and only an opaque single-use nonce is sent to our
 * backend. Our server never sees, logs, or stores the raw PAN.
 */
export const ChargeCardModal = ({ invoice, onClose, onSuccess }) => {
  const [config, setConfig] = useState({ status: "loading" });
  const [card, setCard] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [email, setEmail] = useState("");
  const [amountDollars, setAmountDollars] = useState(((invoice.totals.balance_cents || 0) / 100).toFixed(2));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const cardRef = useRef(null);

  const balanceCents = invoice.totals.balance_cents || 0;
  const enteredCents = Math.round(parseFloat(amountDollars || "0") * 100);

  // Fetch authnet config (public client key + environment) once open
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/authnet/status`, tokenCfg());
        if (!alive) return;
        if (!data.configured || !data.public_client_key || !data.api_login_id) {
          setConfig({ status: "unconfigured" });
          return;
        }
        await loadAcceptJs(data.environment);
        if (!alive) return;
        setConfig({ status: "ready", ...data });
        // Focus the card field so keyboard entry starts immediately.
        setTimeout(() => cardRef.current?.focus(), 100);
      } catch (e) {
        if (!alive) return;
        setConfig({ status: "error", detail: e?.response?.data?.detail || e.message });
      }
    })();
    return () => { alive = false; };
  }, []);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (config.status !== "ready") return false;
    if (digitsOnly(card).length < 13) return false;
    if (!/^\d{1,2}$/.test(expMonth) || +expMonth < 1 || +expMonth > 12) return false;
    if (!/^\d{2,4}$/.test(expYear)) return false;
    if (enteredCents <= 0 || enteredCents > balanceCents) return false;
    return true;
  }, [card, expMonth, expYear, enteredCents, balanceCents, submitting, config.status]);

  const submit = () => {
    setSubmitting(true);
    setError("");

    const yearFull = expYear.length === 2 ? `20${expYear}` : expYear;
    const secureData = {
      authData: {
        clientKey: config.public_client_key,
        apiLoginID: config.api_login_id,
      },
      cardData: {
        cardNumber: digitsOnly(card),
        month: String(parseInt(expMonth, 10)).padStart(2, "0"),
        year: yearFull,
        cardCode: cvv || undefined,
      },
    };

    window.Accept.dispatchData(secureData, async (response) => {
      try {
        if (response.messages.resultCode !== "Ok") {
          const msg = response.messages.message?.[0]?.text || "Card tokenization failed";
          setError(msg);
          setSubmitting(false);
          return;
        }
        const last4 = digitsOnly(card).slice(-4);
        // At this point we send ONLY the opaque nonce to the backend.
        // Immediately clear card fields so the raw PAN doesn't linger in
        // React state or the DOM.
        setCard("");
        setCvv("");

        const { data } = await axios.post(
          `${API}/billing/invoices/${invoice._id}/charge`,
          {
            opaque_data: response.opaqueData,
            card_last4: last4,
            amount_cents: enteredCents,
            customer_email: email || undefined,
          },
          tokenCfg(),
        );
        onSuccess?.(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Charge failed");
      } finally {
        setSubmitting(false);
      }
    });
  };

  return (
    <div
      data-testid="charge-card-modal"
      className="fixed inset-0 z-50 bg-[#0E1C15]/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] max-w-lg w-full p-6 "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading text-[#2A1C15] text-xl">Charge Card</h3>
            </div>
            <div className="text-[#5E4D40] text-xs">
              {invoice.number} · {invoice.client_name} · Balance {money(balanceCents)}
            </div>
          </div>
          <button data-testid="charge-close" onClick={onClose} className="text-[#5E4D40] hover:text-[#5C1A24]" aria-label="close"><X size={20} /></button>
        </div>

        <div className="bg-[#14281E]/8 border border-[#14281E]/25 text-[#14281E] px-3 py-2 mb-4 text-xs flex items-start gap-2">
          <ShieldCheck size={16} weight="bold" className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-heading tracking-widest uppercase text-[0.75rem] mb-0.5">
              Card tokenized in your browser (PCI SAQ A-EP)
            </div>
            The number never leaves your device — Accept.js sends only a one-time nonce to our server.
            {config.status === "ready" && config.environment !== "production" && (
              <div className="text-[#6E5022] mt-1">Sandbox mode — no real money will move.</div>
            )}
          </div>
        </div>

        {config.status === "loading" && (
          <div className="text-[#5E4D40] text-xs py-6 text-center">Loading Authorize.net Accept.js…</div>
        )}

        {config.status === "unconfigured" && (
          <div className="text-[#5C1A24] text-xs border border-[#5C1A24]/30 px-3 py-2">
            Authorize.net isn&apos;t connected yet. Add AUTHNET_API_LOGIN_ID, AUTHNET_TRANSACTION_KEY, and AUTHNET_PUBLIC_CLIENT_KEY to your deployment secrets.
          </div>
        )}

        {config.status === "error" && (
          <div className="text-[#5C1A24] text-xs border border-[#5C1A24]/30 px-3 py-2">
            {config.detail || "Could not initialize Accept.js"}
          </div>
        )}

        {config.status === "ready" && (
          <>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="bj-label block mb-1">Card Number</label>
                <input
                  ref={cardRef}
                  data-testid="charge-card-number"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={formatCardDisplay(card)}
                  onChange={(e) => setCard(digitsOnly(e.target.value).slice(0, 19))}
                  placeholder="4111 1111 1111 1111"
                  className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum focus:outline-none focus:border-[#C8A46A]"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="bj-label block mb-1">MM</label>
                  <input data-testid="charge-exp-month" type="text" inputMode="numeric" maxLength={2}
                    value={expMonth} onChange={(e) => setExpMonth(digitsOnly(e.target.value).slice(0, 2))}
                    placeholder="12" className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
                </div>
                <div>
                  <label className="bj-label block mb-1">YYYY</label>
                  <input data-testid="charge-exp-year" type="text" inputMode="numeric" maxLength={4}
                    value={expYear} onChange={(e) => setExpYear(digitsOnly(e.target.value).slice(0, 4))}
                    placeholder="2028" className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
                </div>
                <div>
                  <label className="bj-label block mb-1">CVV</label>
                  <input data-testid="charge-cvv" type="text" inputMode="numeric" maxLength={4}
                    value={cvv} onChange={(e) => setCvv(digitsOnly(e.target.value).slice(0, 4))}
                    placeholder="123" className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
                </div>
              </div>
              <div>
                <label className="bj-label block mb-1">Client Email (receipt)</label>
                <input data-testid="charge-email" type="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@example.com" className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="bj-label block mb-1">Amount ($)</label>
                <input data-testid="charge-amount" type="number" step="0.01" min="0.01" max={(balanceCents / 100).toFixed(2)}
                  value={amountDollars} onChange={(e) => setAmountDollars(e.target.value)}
                  className="w-full bg-[#E7DDCB] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
                <div className="text-[#5E4D40] text-[0.75rem] mt-1">Max: {money(balanceCents)}</div>
              </div>
            </div>

            {error && (
              <div data-testid="charge-error" className="mt-3 text-[#5C1A24] text-xs border border-[#5C1A24]/30 px-3 py-2">
                {error}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="charge-cancel" onClick={onClose} className="px-4 py-2 border border-[rgba(42,28,21,0.18)] text-[#5E4D40] font-heading tracking-widest text-[0.75rem] uppercase hover:text-[#2A1C15]">
                Cancel
              </button>
              <button
                data-testid="charge-submit"
                onClick={submit}
                disabled={!canSubmit}
                className="px-4 py-2 bg-[#14281E] hover:bg-[#14281E] text-[#F2EBDD] font-heading tracking-widest text-[0.75rem] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Tokenizing & charging…" : `Charge ${money(enteredCents)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
